import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, User, Settings, BookOpen, Heart, LogOut, Menu, X, Infinity } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';
import { NotificationBell } from './NotificationBell';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { currentLanguage, setCurrentLanguage, availableLanguages } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const handleSignOut = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();
      setShowUserDropdown(false);
      setIsLoggingOut(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      setIsLoggingOut(false);
    }
  };

  const navigateToProfile = (tabName?: string) => {
    if (tabName) {
      navigate('/profile', { state: { activeTab: tabName } });
    } else {
      navigate('/profile');
    }
    setShowUserDropdown(false);
  };

  const navigateToSettings = (tabName?: string) => {
    if (tabName) {
      navigate('/settings', { state: { activeTab: tabName } });
    } else {
      navigate('/settings');
    }
    setShowUserDropdown(false);
  };

  // Get user's display name
  const getUserDisplayName = () => {
    if (!user) return '';
    
    // First try to get from user metadata
    if (user.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    
    // Then try to get from profile
    if (user.profile?.full_name) {
      return user.profile.full_name;
    }
    
    // Fallback to email
    return user.email?.split('@')[0] || 'User';
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-earth-sand via-white to-earth-clay">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-earth-clay/50 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-earth-bronze to-earth-terracotta rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                <Heart size={16} className="sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-earth-stone to-earth-bronze bg-clip-text text-transparent">{t('app.name')}</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
              <Link
                to="/"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  isActive('/') ? 'bg-earth-bronze/10 text-earth-bronze' : 'text-gray-700 hover:bg-earth-sand hover:text-earth-bronze'
                }`}
              >
                {t('navigation.home')}
              </Link>
              <Link
                to="/library"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  isActive('/library') ? 'bg-earth-bronze/10 text-earth-bronze' : 'text-gray-700 hover:bg-earth-sand hover:text-earth-bronze'
                }`}
              >
                {t('navigation.library')}
              </Link>
              <Link
                to="/commitment"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  isActive('/commitment') ? 'bg-earth-bronze/10 text-earth-bronze' : 'text-gray-700 hover:bg-earth-sand hover:text-earth-bronze'
                }`}
              >
                {t('navigation.commitment')}
              </Link>
              <Link
                to="/charities"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  isActive('/charities') ? 'bg-earth-bronze/10 text-earth-bronze' : 'text-gray-700 hover:bg-earth-sand hover:text-earth-bronze'
                }`}
              >
                {t('navigation.dana')}
              </Link>
              {user && (
                <button
                  onClick={() => navigateToProfile()}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    isActive('/profile') ? 'bg-earth-bronze/10 text-earth-bronze' : 'text-gray-700 hover:bg-earth-sand hover:text-earth-bronze'
                  }`}
                >
                  {t('navigation.profile')}
                </button>
              )}
            </nav>

            {/* Desktop Right Section */}
            <div className="hidden lg:flex items-center space-x-3 xl:space-x-4">
              {/* Notification Bell - Only show for logged in users */}
              {user && <NotificationBell />}
              
              {/* Language Selector */}
              <div className="relative" ref={languageDropdownRef}>
                <button
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  className="language-selector-button flex items-center space-x-1 sm:space-x-2 rounded-full px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm transition-all duration-200"
                >
                  <span className="text-base">{currentLanguage.flag}</span>
                  <span className="font-medium hidden sm:inline">{currentLanguage.name}</span>
                  <ChevronDown size={10} className="sm:w-3 sm:h-3 text-white/70 transition-transform duration-200 ${showLanguageDropdown ? 'rotate-180' : ''}" />
                </button>

                {showLanguageDropdown && (
                  <div className="language-dropdown absolute top-full mt-2 right-0 rounded-lg py-2 z-20 min-w-[120px] sm:min-w-[140px]">
                    {availableLanguages.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => {
                          setCurrentLanguage(language);
                          setShowLanguageDropdown(false);
                        }}
                        className="w-full flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 transition-colors duration-150"
                      >
                        <span className="text-base">{language.flag}</span>
                        <span className="text-xs sm:text-sm text-gray-700">{language.name}</span>
                        {currentLanguage.code === language.code && (
                          <div className="ml-auto w-2 h-2 bg-purple-500 rounded-full"></div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* User Section */}
              {user ? (
                <div className="relative" ref={userDropdownRef}>
                  <button
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className="flex items-center space-x-1 sm:space-x-2 bg-earth-clay hover:bg-earth-terracotta/30 rounded-full px-2 sm:px-3 py-1 sm:py-2 transition-colors duration-200"
                  >
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-earth-bronze to-earth-terracotta rounded-full flex items-center justify-center">
                      <User size={12} className="sm:w-4 sm:h-4 text-white" />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-gray-700 hidden sm:inline">
                      {getUserDisplayName()}
                    </span>
                    <ChevronDown size={10} className="sm:w-3 sm:h-3 text-gray-500 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}" />
                  </button>

                  {showUserDropdown && (
                    <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-20 min-w-[160px] sm:min-w-[180px]">
                      <button
                        onClick={() => navigateToProfile()}
                        className="flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 hover:bg-gray-50 transition-colors duration-150 w-full text-left"
                      >
                        <User size={14} className="sm:w-4 sm:h-4 text-gray-500" />
                        <span className="text-xs sm:text-sm text-gray-700">{t('navigation.profile')}</span>
                      </button>
                      <button
                        onClick={() => navigateToSettings()}
                        className="flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 hover:bg-gray-50 transition-colors duration-150 w-full text-left"
                      >
                        <Settings size={14} className="sm:w-4 sm:h-4 text-gray-500" />
                        <span className="text-xs sm:text-sm text-gray-700">{t('navigation.settings')}</span>
                      </button>
                      <hr className="my-2 border-gray-100" />
                      <button
                        onClick={handleSignOut}
                        disabled={isLoggingOut}
                        className="flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 hover:bg-gray-50 transition-colors duration-150 w-full text-left disabled:opacity-50"
                      >
                        <LogOut size={14} className="sm:w-4 sm:h-4 text-gray-500" />
                        <span className="text-xs sm:text-sm text-gray-700">
                          {isLoggingOut ? 'Logging out...' : t('navigation.logout')}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <Link
                    to="/login"
                    className="text-xs sm:text-sm font-medium text-gray-700 hover:text-earth-bronze transition-colors duration-200"
                  >
                    {t('navigation.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="bg-gradient-to-r from-earth-bronze to-earth-terracotta text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium hover:from-earth-warmstone hover:to-earth-bronze transition-all duration-200"
                  >
                    {t('navigation.signup')}
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-3">
              {/* Mobile Notification Bell */}
              {user && <NotificationBell />}
              
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-1 sm:p-2 text-gray-700 hover:text-purple-600 transition-colors duration-200"
              >
                {showMobileMenu ? <X size={20} className="sm:w-6 sm:h-6" /> : <Menu size={20} className="sm:w-6 sm:h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {showMobileMenu && (
            <div className="lg:hidden border-t border-earth-clay py-4">
              <nav className="flex flex-col space-y-3 sm:space-y-4 px-2">
                <Link 
                  to="/" 
                  className={`text-sm font-medium transition-colors duration-200 py-1 ${
                    isActive('/') ? 'text-earth-bronze' : 'text-gray-700'
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t('navigation.home')}
                </Link>
                <Link 
                  to="/library" 
                  className={`text-sm font-medium transition-colors duration-200 py-1 ${
                    isActive('/library') ? 'text-earth-bronze' : 'text-gray-700'
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t('navigation.library')}
                </Link>
                <Link 
                  to="/commitment" 
                  className={`text-sm font-medium transition-colors duration-200 py-1 ${
                    isActive('/commitment') ? 'text-earth-bronze' : 'text-gray-700'
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t('navigation.commitment')}
                </Link>
                <Link 
                  to="/charities" 
                  className={`text-sm font-medium transition-colors duration-200 py-1 ${
                    isActive('/charities') ? 'text-earth-bronze' : 'text-gray-700'
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t('navigation.dana')}
                </Link>
                {user ? (
                  <>
                    <button 
                      onClick={() => {
                        navigateToProfile();
                        setShowMobileMenu(false);
                      }}
                      className={`text-sm font-medium transition-colors duration-200 text-left py-1 ${
                        isActive('/profile') ? 'text-earth-bronze' : 'text-gray-700'
                      }`}
                    >
                      {t('navigation.profile')}
                    </button>
                    <button 
                      onClick={() => {
                        navigateToSettings();
                        setShowMobileMenu(false);
                      }}
                      className={`text-sm font-medium transition-colors duration-200 text-left py-1 ${
                        isActive('/settings') ? 'text-earth-bronze' : 'text-gray-700'
                      }`}
                    >
                      {t('navigation.settings')}
                    </button>
                    <button
                      onClick={handleSignOut}
                      disabled={isLoggingOut}
                      className="text-sm font-medium text-gray-700 text-left disabled:opacity-50 py-1"
                    >
                      {isLoggingOut ? 'Logging out...' : t('navigation.logout')}
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col space-y-2 pt-2">
                    <Link
                      to="/login"
                      className="text-sm font-medium text-gray-700 py-1"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      {t('navigation.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="bg-gradient-to-r from-earth-bronze to-earth-terracotta text-white px-4 py-2 rounded-full text-sm font-medium hover:from-earth-warmstone hover:to-earth-bronze transition-all duration-200 text-center"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      {t('navigation.signup')}
                    </Link>
                  </div>
                )}
                
                {/* Mobile Language Selector */}
                <div className="pt-3 sm:pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 py-1">{t('navigation.language')}</span>
                    <div className="relative">
                      <button
                        onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                        className="flex items-center space-x-2 bg-earth-clay rounded-full px-2 sm:px-3 py-1 text-xs sm:text-sm"
                      >
                        <span>{currentLanguage.flag}</span>
                        <span>{currentLanguage.name}</span>
                        <ChevronDown size={10} className="sm:w-3 sm:h-3 transition-transform duration-200 ${showLanguageDropdown ? 'rotate-180' : ''}" />
                      </button>
                      
                      {showLanguageDropdown && (
                        <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-20 min-w-[120px] sm:min-w-[140px]">
                          {availableLanguages.map((language) => (
                            <button
                              key={language.code}
                              onClick={() => {
                                setCurrentLanguage(language);
                                setShowLanguageDropdown(false);
                                setShowMobileMenu(false);
                              }}
                              className="w-full flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                            >
                              <span className="text-base">{language.flag}</span>
                              <span className="text-xs sm:text-sm text-gray-700">{language.name}</span>
                              {currentLanguage.code === language.code && (
                                <div className="ml-auto w-2 h-2 bg-earth-bronze rounded-full"></div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      <footer className="bg-white/50 backdrop-blur-sm border-t border-earth-clay/30 py-6 sm:py-8 text-center text-gray-600 text-xs sm:text-sm px-4 mt-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-8">
            <p className="font-medium">© {new Date().getFullYear()} Manifestation with Manivo. All rights reserved.</p>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <Link
                to="/terms"
                className="text-gray-500 hover:text-earth-bronze transition-colors duration-200"
              >
                Terms of Service
              </Link>
              <span className="text-gray-300 hidden sm:inline">•</span>
              <Link
                to="/privacy"
                className="text-gray-500 hover:text-earth-bronze transition-colors duration-200"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};